import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { GATEWAYS_DE_PAGAMENTO, descricaoDoGateway, urlDoWebhook } from '@/data/gateways';
import { dados } from '@/data/porta';
import { pode } from '@/session/sessao';
import type { AmbienteDoGateway, GatewayDePagamento, ProvedorDeGateway } from '@/data/tipos';

const ROTULO_DO_AMBIENTE: Record<AmbienteDoGateway, string> = { sandbox: 'Sandbox', producao: 'Produção' };

/**
 * Os gateways de pagamento da plataforma (trilha T9), na tela de Integrações.
 *
 * Os três ficam configurados lado a lado e UM é o ativo: é por ele que saem as
 * cobranças novas. Cada fatura lembra o gateway que a cobrou, e o aviso de
 * pagamento dos três continua aceito — trocar o ativo não perde baixa de ninguém.
 *
 * A chave e o segredo do webhook entram e não voltam: ficam no cofre, e a tela
 * recebe só que existem e os quatro últimos caracteres da chave. "Testar conexão"
 * faz uma leitura barata no gateway, e só quando o operador clica.
 */
export function GatewaysDePagamento({ gateways }: { gateways: GatewayDePagamento[] }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();

    const [emEdicao, setEmEdicao] = useState<ProvedorDeGateway | null>(null);
    const [ambiente, setAmbiente] = useState<AmbienteDoGateway>('sandbox');
    const [chave, setChave] = useState('');
    const [publicavel, setPublicavel] = useState('');
    const [segredo, setSegredo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [testando, setTestando] = useState<ProvedorDeGateway | null>(null);
    const sujo = useAlteracaoPendente({ ambiente, chave, publicavel, segredo }, emEdicao);

    const podeEditar = pode('integracoes.editar');
    const ativo = gateways.find((g) => g.ativo);
    const atualEmEdicao = emEdicao ? gateways.find((g) => g.provedor === emEdicao) : undefined;
    const descricao = emEdicao ? descricaoDoGateway(emEdicao) : undefined;

    /* A chave é exigida na primeira vez e ao trocar de ambiente: a de sandbox não
       vale em produção, e o banco recusa a troca sem chave nova. */
    const precisaDeChave = !atualEmEdicao?.temChave || atualEmEdicao.ambiente !== ambiente;
    /* A publicável segue a mesma regra da secreta, e por um motivo a mais: a de teste e a
       de produção não se misturam, e o banco recusa a do ambiente errado (0170). */
    const precisaDePublicavel = Boolean(descricao?.publicavel)
        && (!atualEmEdicao?.chavePublicavel || atualEmEdicao.ambiente !== ambiente);
    const nadaMudou = !chave.trim() && !segredo.trim() && !publicavel.trim() && atualEmEdicao?.ambiente === ambiente;
    const podeGuardar = !salvando && !nadaMudou && (!precisaDeChave || chave.trim().length >= 16)
        && (!precisaDePublicavel || publicavel.trim().length >= 16)
        && (!chave.trim() || chave.trim().length >= 16) && (!segredo.trim() || segredo.trim().length >= 16);

    const abrir = (provedor: ProvedorDeGateway) => {
        const atual = gateways.find((g) => g.provedor === provedor);
        setEmEdicao(provedor);
        setAmbiente(atual?.ambiente ?? 'sandbox');
        /* Vazios sempre: não há chave nem segredo para trazer de volta. A publicável até
           poderia voltar (é pública), mas trazê-la preenchida faria "guardar" regravá-la
           sem ninguém ter pedido — e o campo vazio já diz, na dica, qual está lá. */
        setChave('');
        setPublicavel('');
        setSegredo('');
        setErro(null);
    };

    const guardar = async () => {
        if (!emEdicao) return;
        setSalvando(true);
        setErro(null);
        try {
            await dados().salvarGateway(emEdicao, { ambiente, chave, segredoWebhook: segredo, chavePublicavel: publicavel });
            aviso({ title: 'Gateway guardado', description: `${descricaoDoGateway(emEdicao).nome} · cifrado no cofre` });
            setEmEdicao(null);
            setChave('');
            setPublicavel('');
            setSegredo('');
            revalidator.revalidate();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Tente de novo.');
        } finally {
            setSalvando(false);
        }
    };

    const testar = async (provedor: ProvedorDeGateway) => {
        setTestando(provedor);
        try {
            const r = await dados().testarGateway(provedor);
            aviso({
                title: r.ok ? 'Conexão confirmada' : 'A conexão não foi confirmada',
                description: r.mensagem,
                tone: r.ok ? 'success' : 'danger',
            });
        } catch (e) {
            aviso({ title: 'A conexão não foi confirmada', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setTestando(null);
        }
    };

    const ativar = async (provedor: ProvedorDeGateway | null) => {
        const nome = provedor ? descricaoDoGateway(provedor).nome : '';
        const ok = await confirmar(provedor ? {
            titulo: `Cobrar as próximas faturas pelo ${nome}?`,
            descricao: 'As faturas já cobradas continuam no gateway delas, e os avisos de pagamento delas continuam dando baixa.',
            confirmar: `Ativar ${nome}`,
        } : {
            titulo: 'Desligar a cobrança online?',
            descricao: 'As faturas novas passam a ser cobradas à mão, com a baixa registrada no Faturamento. As cobranças já criadas continuam valendo.',
            confirmar: 'Desligar',
        });
        if (!ok) return;
        try {
            await dados().definirGatewayAtivo(provedor);
            aviso({ title: provedor ? `${nome} ativo` : 'Cobrança online desligada', description: provedor ? 'as próximas faturas saem por ele' : 'a baixa volta a ser manual' });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível trocar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const remover = async (provedor: ProvedorDeGateway) => {
        const nome = descricaoDoGateway(provedor).nome;
        const ok = await confirmar({
            titulo: `Remover o ${nome}?`,
            descricao: 'A chave e o segredo do webhook saem do cofre. O banco recusa enquanto houver fatura em aberto cobrada por ele.',
            confirmar: 'Remover',
        });
        if (!ok) return;
        try {
            await dados().removerGateway(provedor);
            aviso({ title: `${nome} removido`, description: 'chave e segredo apagados do cofre' });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível remover', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const copiar = async (texto: string) => {
        try {
            await navigator.clipboard.writeText(texto);
            aviso({ title: 'URL copiada', description: 'cole no cadastro de webhook do gateway' });
        } catch {
            aviso({ title: 'Não foi possível copiar', description: texto, tone: 'danger' });
        }
    };

    return (
        <Card title="Gateways de pagamento" subtitle="Por onde as clínicas pagam as faturas da assinatura" padding={18}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {ativo ? (
                    <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                        As cobranças novas saem pelo <strong>{descricaoDoGateway(ativo.provedor).nome}</strong> ({ROTULO_DO_AMBIENTE[ativo.ambiente].toLowerCase()}).
                        Cada fatura lembra o gateway que a cobrou, e os avisos dos três continuam aceitos.
                    </p>
                ) : (
                    <Alert tone="warning" dismissible={false} title="Cobrança online desligada">
                        Nenhum gateway está ativo: as faturas são cobradas à mão e a baixa é registrada no Faturamento.
                        Guarde a chave e o segredo do webhook de um gateway, cadastre a URL abaixo no painel dele e ative-o.
                    </Alert>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(300px,100%),1fr))', gap: 12 }}>
                    {GATEWAYS_DE_PAGAMENTO.map((d) => {
                        const g = gateways.find((x) => x.provedor === d.id);
                        const url = urlDoWebhook(d.id);
                        return (
                            <section
                                key={d.id} aria-label={d.nome}
                                style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius-card, 14px)' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <Icon name="credit-card" size={16} />
                                    <strong style={{ fontSize: 14, fontWeight: 600 }}>{d.nome}</strong>
                                    <Badge tone={g?.ativo ? 'success' : 'neutral'} dot>
                                        {g?.ativo ? 'Ativo' : g?.temChave ? 'Em reserva' : 'Sem chave'}
                                    </Badge>
                                    {g?.temChave && <Badge tone={g.ambiente === 'producao' ? 'warning' : 'neutral'}>{ROTULO_DO_AMBIENTE[g.ambiente]}</Badge>}
                                </div>

                                <p style={{ margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                                    {g?.temChave ? (
                                        <>
                                            {d.chave.rotulo} terminada em <code>{g.dica}</code>
                                            <br />
                                            {g.temSegredoWebhook ? 'Segredo do webhook guardado' : d.segredo.exigido ? 'Falta o segredo do webhook — sem ele o pagamento não dá baixa' : 'Sem segredo do webhook (opcional)'}
                                        </>
                                    ) : d.comoPaga}
                                </p>

                                <div style={{ fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                                    <span style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)' }}>URL do webhook</span>
                                    {url ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                            <code style={{ wordBreak: 'break-all' }}>{url}</code>
                                            <Button size="sm" variant="ghost" onClick={() => { void copiar(url); }} aria-label={`Copiar a URL do webhook do ${d.nome}`}>Copiar</Button>
                                        </span>
                                    ) : (
                                        <span style={{ display: 'block' }}>Aparece quando a aplicação está ligada ao servidor.</span>
                                    )}
                                    <span style={{ display: 'block', marginTop: 4 }}>Eventos: {d.eventos}</span>
                                    <a href={d.documentacao} target="_blank" rel="noreferrer" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                                        Documentação do webhook
                                    </a>
                                </div>

                                {podeEditar && (
                                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        <Button size="sm" variant="secondary" onClick={() => abrir(d.id)} aria-label={`${g?.temChave ? 'Trocar' : 'Configurar'} o ${d.nome}`}>
                                            {g?.temChave ? 'Trocar' : 'Configurar'}
                                        </Button>
                                        {g?.temChave && (
                                            <Button size="sm" variant="ghost" disabled={testando !== null} onClick={() => { void testar(d.id); }}>
                                                {testando === d.id ? 'Testando…' : 'Testar conexão'}
                                            </Button>
                                        )}
                                        {g?.temChave && !g.ativo && (
                                            <Button size="sm" variant="ghost" onClick={() => { void ativar(d.id); }}>Ativar</Button>
                                        )}
                                        {g?.ativo && (
                                            <Button size="sm" variant="ghost" onClick={() => { void ativar(null); }}>Desativar</Button>
                                        )}
                                        {g?.temChave && !g.ativo && (
                                            <Button size="sm" variant="ghost" onClick={() => { void remover(d.id); }}>Remover</Button>
                                        )}
                                    </span>
                                )}
                            </section>
                        );
                    })}
                </div>
            </div>

            {confirmacao}

            <Modal
                open={emEdicao !== null}
                title={descricao ? `Gateway ${descricao.nome}` : 'Gateway de pagamento'}
                onClose={() => setEmEdicao(null)}
                dirty={sujo}
                width={580}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setEmEdicao(null)}>Cancelar</Button>
                        <Button onClick={() => { void guardar(); }} disabled={!podeGuardar}>
                            {salvando ? 'Guardando…' : 'Guardar'}
                        </Button>
                    </>
                )}
            >
                {descricao && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {erro && <Alert tone="danger" dismissible={false} title="Não foi possível guardar">{erro}</Alert>}

                        <FormField label="Ambiente" htmlFor="gw-ambiente" required hint="Comece pelo sandbox: nada é cobrado de verdade.">
                            <Select
                                id="gw-ambiente" value={ambiente} onChange={(v) => setAmbiente(v === 'producao' ? 'producao' : 'sandbox')}
                                options={[{ value: 'sandbox', label: 'Sandbox (teste)' }, { value: 'producao', label: 'Produção' }]}
                            />
                        </FormField>

                        <FormField
                            label={descricao.chave.rotulo} htmlFor="gw-chave" required={precisaDeChave}
                            hint={precisaDeChave ? descricao.chave.dica : `Em branco, fica a chave terminada em ${atualEmEdicao?.dica ?? '…'}. ${descricao.chave.dica}`}
                        >
                            <Input id="gw-chave" type="password" autoComplete="off" value={chave} onChange={setChave} />
                        </FormField>

                        {descricao.publicavel && (
                            <FormField
                                label={descricao.publicavel.rotulo} htmlFor="gw-publicavel" required={precisaDePublicavel}
                                hint={atualEmEdicao?.chavePublicavel && !precisaDePublicavel
                                    ? `Em branco, fica ${atualEmEdicao.chavePublicavel}. ${descricao.publicavel.dica}`
                                    : descricao.publicavel.dica}
                            >
                                {/* Sem `type="password"`: esconder o que vai ao navegador de
                                    todo visitante só esconderia de quem está configurando —
                                    e ver a chave é o que evita colar a secreta aqui. */}
                                <Input id="gw-publicavel" autoComplete="off" value={publicavel} onChange={setPublicavel} />
                            </FormField>
                        )}

                        <FormField
                            label={descricao.segredo.rotulo} htmlFor="gw-segredo" required={descricao.segredo.exigido && !atualEmEdicao?.temSegredoWebhook}
                            hint={atualEmEdicao?.temSegredoWebhook ? `Em branco, fica o guardado. ${descricao.segredo.dica}` : descricao.segredo.dica}
                        >
                            <Input id="gw-segredo" type="password" autoComplete="off" value={segredo} onChange={setSegredo} />
                        </FormField>

                        <p style={{ margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                            Os dois ficam cifrados no cofre do banco e só as funções da borda os leem. Guardar não chama o
                            gateway: para conferir a chave, use <strong>Testar conexão</strong> depois.
                        </p>
                    </div>
                )}
            </Modal>
        </Card>
    );
}
